import LogoutIcon from '@mui/icons-material/LogoutOutlined';
import BalanceIcon from '@mui/icons-material/BalanceOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNewOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlineOutlined';
import { Menu, Divider, MenuItem, Typography } from '@mui/material';
import { HoverMenuItem } from '@nexus/ari-lib';

// ---------------------------------------------------------------------------

const LEGAL_LINKS = [
  {
    label: 'Cloud License Agreement',
    url: 'https://realwear.com/legal/cloud-license-agreement',
  },
  {
    label: 'Terms of Sale',
    url: 'https://realwear.com/legal/terms-of-sale',
  },
  {
    label: 'Privacy Policy',
    url: 'https://realwear.com/legal/privacy-policy',
  },
  {
    label: 'Data Processing Addendum',
    url: 'https://realwear.com/legal/data-processing-addendum',
  },
];

const menuItemSx = {
  px: 1.5,
  py: 1,
  minHeight: 40,
  borderRadius: 1,
  gap: 1.5,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const iconSx = { color: 'grey.400', fontSize: 20 };

// ---------------------------------------------------------------------------

interface AccountMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onAccountClick: () => void;
  onSignOutClick: () => void;
}

export function AccountMenu({
  anchorEl,
  open,
  onClose,
  onAccountClick,
  onSignOutClick,
}: AccountMenuProps) {
  const openLink = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    onClose();
  };

  const legalSubmenu = LEGAL_LINKS.map((link) => (
    <MenuItem
      key={link.url}
      onClick={() => openLink(link.url)}
      sx={menuItemSx}
    >
      <Typography variant="body2" sx={{ flex: 1 }}>
        {link.label}
      </Typography>
      <OpenInNewIcon fontSize="small" sx={iconSx} />
    </MenuItem>
  ));

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          sx: (theme) => ({
            width: 164,
            p: 0.25,
            borderRadius: 1,
            bgcolor: 'container.low',
            boxShadow: theme.customShadows.dropdown,
            mt: theme.spacing(1),
          }),
        },
        list: {
          sx: { p: 0, display: 'flex', flexDirection: 'column' },
        },
      }}
    >
      <MenuItem
        onClick={() => {
          onAccountClick();
          onClose();
        }}
        sx={menuItemSx}
        data-testid="cloud.account-menu.account"
      >
        <Typography variant="body2" sx={{ flex: 1 }}>
          Account
        </Typography>
        <PersonOutlineIcon sx={iconSx} />
      </MenuItem>

      <HoverMenuItem
        submenu={legalSubmenu}
        submenuPosition="left"
        submenuPaperSx={{ minWidth: 180, bgcolor: 'container.low' }}
        sx={menuItemSx}
        hideChevron
      >
        <Typography variant="body2" sx={{ flex: 1 }}>
          Legal
        </Typography>
        <BalanceIcon sx={iconSx} />
      </HoverMenuItem>

      <Divider sx={{ my: 0 }} />

      <MenuItem
        onClick={onSignOutClick}
        sx={menuItemSx}
        data-testid="cloud.account-menu.logout"
      >
        <Typography variant="body2" sx={{ flex: 1 }}>
          Log Out
        </Typography>
        <LogoutIcon sx={iconSx} />
      </MenuItem>
    </Menu>
  );
}
